import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Select, Popover } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { AddtoFlowEventFloatWrapper, PopWrapper, PopHeader, PopFooter } from './styled';
import { useApplyEventDraft } from '@src/hooks/useNewWorkflow';
import { KeyMap } from '@src/emp-3.0/utils/constants';
import { useLocation } from 'react-router-dom';
import { ApplyFilterAsEvent, EventRuleTypes } from '@src/common/types/empTypes';
import { useExplorerStore } from '../store';
import { useMetricBuilderStore } from '@src/emp-3.0/components/Metrics/IndividualMetric/store';
import { EMetricTypes, EStringOp } from '@src/emp-3.0/types';

const unSupportedKeys = [
  'page_urlquery',
  'referrer',
  'user_id',
  'refr_urlhost',
  'ip_isp',
  'client_id',
];

const FlowEventModal = ({ event }: { event: any }) => {
  const { setAutoScroll } = useExplorerStore();
  const { metricType } = useMetricBuilderStore();
  const location = useLocation();
  const path = location.pathname;
  const isFlowPage = useMemo(() => path.includes(`/workflow/flow`), [path]);

  const options = useMemo(() => {
    let options: { label: string; value: ApplyFilterAsEvent }[] = [
      { label: `Initial Event`, value: 'initial' },
      { label: 'Complete Event', value: 'complete' },
    ];
    if (!isFlowPage) {
      if (metricType === EMetricTypes.SINGLE_EVENT) {
        options = [{ label: `Event`, value: 'initial' }];
      } else if (metricType === EMetricTypes.RATIO_METRIC) {
        options = [
          { label: 'Numerator Event', value: 'numerator' },
          { label: 'Denominator Event', value: 'denominator' },
        ];
      }
    } else {
      options.push({ label: 'New Error Event', value: 'error' });
    }
    return options;
  }, [isFlowPage, metricType]);

  const applyEventDraft = useApplyEventDraft(isFlowPage);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [applyAs, setApplyAs] = useState<ApplyFilterAsEvent>(options[0].value);
  useEffect(() => {
    setApplyAs(options[0].value);
  }, [options]);

  const [checkedList, setCheckedList] = useState([] as any[]);

  const renderLabel = (left: string, right: string) => {
    return (
      <div className="checkbox-label">
        <span>{`"${left}":`}&nbsp;</span>
        <div>{` "${right}"`}</div>
      </div>
    );
  };

  const items = useMemo(() => {
    return Object.keys(event.eventAttributes).map((key) => {
      const pureKey = key.replace('custom_tag:', '').replace('metadata:', '');
      return {
        label: renderLabel(key, event.eventAttributes[key]),
        value: key,
        disabled: unSupportedKeys.includes(pureKey),
      };
    });
  }, [event]);

  const showModal = () => {
    setIsModalVisible(true);
    setAutoScroll(false);
  };

  const handleApply = () => {
    setIsModalVisible(false);
    setAutoScroll(true);
    const metadata = [] as any;
    const mapping_rules: EventRuleTypes[][] = [];
    checkedList.forEach((key: string) => {
      const subAndRules: EventRuleTypes[] = [];
      if (key?.includes('metadata:')) {
        metadata.push([
          {
            key: key.split('metadata:')?.[1],
            op: EStringOp.EQ,
            value: [event.eventAttributes[key]],
          },
        ]);
      } else {
        const key1 = key.replace('custom_tag:', '');
        const revertKey = Object.keys(KeyMap).find((key) => KeyMap[key] === key1) || key1;
        if (event.eventAttributes[key] === '') {
          subAndRules.push({
            tag_key: {
              value: [revertKey],
              op: EStringOp.EQ,
            },
            tag_value: {
              value: [],
              op: EStringOp.IS_EMPTY,
            },
          });
        } else {
          subAndRules.push({
            tag_key: {
              value: [revertKey],
              op: EStringOp.EQ,
            },
            tag_value: {
              value: [event.eventAttributes[key]],
              op: EStringOp.EQ,
            },
          });
        }

        mapping_rules.push(subAndRules);
      }
    });

    const rules = [
      {
        key: 'all_event_name',
        op: EStringOp.EQ,
        value: [event.eventName],
        mapping_rules,
        metadata,
      },
    ];
    // HP-11193 keep name empty
    const draft = {
      event_category: 'Custom',
      event_name: '',
      rules: [[...rules]],
      mode: 'edit',
    };
    applyEventDraft(draft, applyAs);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setAutoScroll(true);
  };

  const onCheckAllChange = (e: any) => {
    setCheckedList(
      e.target.checked ? items.filter((item) => !item.disabled).map((item) => item.value) : [],
    );
  };

  const onCheckboxChange = (checkedValues: any) => {
    setCheckedList(checkedValues);
  };

  const content = (
    <PopWrapper>
      <PopHeader>
        <span>Event:{event.eventName}</span>
        <Button type="text" icon={<CloseOutlined />} onClick={handleCancel} />
      </PopHeader>
      <div className="div-text">Choose items to add:</div>
      <Checkbox className="first-checkbox" onChange={onCheckAllChange}>
        Tag Key and Value
      </Checkbox>
      <Checkbox.Group value={checkedList} onChange={onCheckboxChange}>
        {items.map((item) => (
          <Checkbox
            disabled={item.disabled}
            className={'flow-event-modal-checkbox'}
            key={item.value}
            value={item.value}
          >
            {item.label}
          </Checkbox>
        ))}
      </Checkbox.Group>
      <div className="div-apply">
        <span>Apply As </span>
        <Select
          defaultValue={options[0].value}
          style={{ width: 340 }}
          onChange={(value) => {
            setApplyAs(value);
          }}
          options={options}
        />
      </div>
      <PopFooter>
        <Button type="default" onClick={handleCancel}>
          Cancel
        </Button>
        <Button type="primary" ghost onClick={handleApply}>
          Apply
        </Button>
      </PopFooter>
    </PopWrapper>
  );
  return (
    <AddtoFlowEventFloatWrapper>
      <Popover
        content={content}
        title=""
        onOpenChange={(status) => {
          setAutoScroll(!status);
        }}
        open={isModalVisible}
        trigger="click"
      >
        <div style={{ padding: '8px', marginTop: '-16px', background: '#fff' }}>
          <Button type="primary" ghost onClick={showModal}>
            Apply as…
          </Button>
        </div>
      </Popover>
    </AddtoFlowEventFloatWrapper>
  );
};

export default FlowEventModal;
